"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const lodash_1 = require("lodash");
const core_1 = require("@emotion/core");
const toolbox_error_tracking_1 = require("@zapier/toolbox-error-tracking");
const common_utils_1 = require("@zapier/common-utils");
const responsive_utils_1 = require("@zapier/responsive-utils");
const theme_1 = require("@zapier/theme");
const design_system_1 = require("@zapier/design-system");
const getFieldValuePath_1 = __importDefault(require("./getFieldValuePath"));
const getFieldMapping_1 = __importDefault(require("./getFieldMapping"));
const getFieldValue_1 = __importDefault(require("./getFieldValue"));
const FieldStyles = common_utils_1.labelStyles('FieldsForm', {
    root: {
        marginBottom: 4,
    },
});
function FieldComponentByType({ field, formProps, inputProps }) {
    const { key } = field;
    const { choiceGroups, metadata, onAddSearchStep, onCommitChange, onLoadOptions, shouldFallbackToDefault, value, } = formProps;
    const fieldValue = getFieldValue_1.default({
        formValue: value,
        field,
        shouldFallbackToDefault,
    });
    const [localFieldValue, setLocalFieldValue] = react_1.useState(fieldValue);
    const layout = responsive_utils_1.useMediaProps({
        [theme_1.Screen.queries.phone]: { size: 'small' },
        [theme_1.Screen.queries.phablet]: { size: 'small' },
        [theme_1.Screen.queries.tablet]: { size: 'medium' },
        [theme_1.Screen.queries.desktop]: { size: 'large' },
    });
    const path = getFieldValuePath_1.default(field);
    const commitIfChanged = react_1.useCallback((currentValue) => {
        const areValuesInitialized = !(lodash_1.isNil(currentValue) && lodash_1.isNil(fieldValue));
        if (areValuesInitialized && currentValue !== fieldValue) {
            onCommitChange({
                params: Object.assign(Object.assign({}, value), { [key]: currentValue }),
                field,
                path,
            });
        }
    }, [field, fieldValue, key, onCommitChange, path, value]);
    const updateLocalFieldValue = react_1.useCallback((newValue, options = {}) => {
        setLocalFieldValue(newValue);
        if (options.commit) {
            commitIfChanged(newValue);
        }
    }, [commitIfChanged]);
    const { component: FieldComponent, getComponentProps, onChangeFieldValue = () => { }, } = getFieldMapping_1.default(field);
    const performAddSearchStep = react_1.useCallback(() => onAddSearchStep(field), [
        field,
        onAddSearchStep,
    ]);
    const performChange = react_1.useCallback((...args) => onChangeFieldValue(localFieldValue, updateLocalFieldValue, ...args), [localFieldValue, onChangeFieldValue, updateLocalFieldValue]);
    const performBlur = react_1.useCallback(() => commitIfChanged(localFieldValue), [
        commitIfChanged,
        localFieldValue,
    ]);
    const performLoadOptions = react_1.useCallback((more) => onLoadOptions({ field, path, more }), [field, onLoadOptions, path]);
    const commonProps = {
        choiceGroups,
        commitIfChanged,
        metadata,
        onAddSearchStep: performAddSearchStep,
        onBlur: performBlur,
        onChange: performChange,
        onLoadOptions: performLoadOptions,
        screenSize: layout.size,
    };
    const componentProps = getComponentProps({
        field,
        formProps,
        formValue: value,
        value: localFieldValue,
    });
    return (core_1.jsx("div", { css: FieldStyles.root },
        core_1.jsx(FieldComponent, Object.assign({}, commonProps, componentProps, { id: inputProps.id }))));
}
const errorBoundaryCss = {
    padding: theme_1.Spacing.medium,
    backgroundColor: design_system_1.Colors.red100,
    borderRadius: theme_1.Border.radius.large,
    color: design_system_1.Colors.red500,
    marginBottom: theme_1.Spacing.medium,
};
class FieldComponentErrorBoundary extends react_1.Component {
    constructor() {
        super(...arguments);
        this.state = {};
    }
    trackException(error, data) {
        this.context(error, data);
    }
    componentDidCatch(error, info) {
        const { field } = this.props;
        this.trackException(error, { info, field });
        this.setState({ error, info });
    }
    render() {
        if (this.state.error) {
            return (core_1.jsx("div", { css: errorBoundaryCss },
                "Looks like we had an error with the ",
                this.props.field.label,
                " field. Please refresh your browser and contact support if this continues."));
        }
        return core_1.jsx(FieldComponentByType, Object.assign({}, this.props));
    }
}
FieldComponentErrorBoundary.contextType = toolbox_error_tracking_1.TrackExceptionContext;
const ExportedComponent = FieldComponentErrorBoundary;
exports.default = ExportedComponent;
